import { SignClient } from "@via/walletconnect-sign-client";
import {
  EngineTypes,
  ISignClient,
  PairingTypes,
  ProposalTypes,
  SessionTypes,
  SignClientTypes,
} from "@via/walletconnect-types";

import { DEFAULT_CONNECT_PARAMS, DEFAULT_INIT_PARAMS, VIA_SESSION_OPTIONS_KEY } from "./constants";
import { DefaultStorage } from "./wcStorage";

export type WcSuccessCallback = (
  params: EngineTypes.ConnectParams,
  session: SessionTypes.Struct,
) => void;

export type WcErrorCallback = (params: EngineTypes.ConnectParams, error: Error) => void;

export type WcRequestSuccessCallback = (
  params: EngineTypes.RequestParams,
  response: unknown,
) => void;

export type WcRequestErrorCallback = (params: EngineTypes.RequestParams, error: Error) => void;

export type WcSignClientInitOptions = SignClientTypes.Options & {
  storage?: SignClientTypes.Options["storage"];
};

// SDP dashboard WalletConnect service wrapper.
export class WcSignClient {
  public static async init(
    userId: string,
    options: WcSignClientInitOptions = {},
  ): Promise<WcSignClient> {
    const instance = new WcSignClient(userId, options);
    await instance.initialize();
    return instance;
  }

  private constructor(
    private readonly userId: string,
    private options: WcSignClientInitOptions,
  ) {
    if (!options.storage) {
      options.storage = new DefaultStorage(userId);
    }
    if (!options.metadata) {
      options.metadata = DEFAULT_INIT_PARAMS.metadata;
    }
    if (!options.projectId) {
      options.projectId = DEFAULT_INIT_PARAMS.projectId;
    }
    if (!options.relayUrl) {
      options.relayUrl = DEFAULT_INIT_PARAMS.relayUrl;
    }
    if (!options.logger) {
      options.logger = DEFAULT_INIT_PARAMS.logger;
    }
  }

  private signClient!: InstanceType<typeof SignClient>;
  private pairing: PairingTypes.Struct | null = null;
  private session: SessionTypes.Struct | null = null;

  private async initialize(): Promise<void> {
    this.signClient = await SignClient.init(this.options);

    // Restore the most recent pairing/session (if any) so page reloads keep
    // metadata that WalletConnect already persisted to storage.
    const pairing = this.signClient.core.pairing.getPairings().pop() ?? null;
    if (pairing) this.pairing = pairing;

    const session = this.signClient.session.getAll().pop() ?? null;
    if (session) this.session = session;

    // Keep our cached session pointer in sync with lifecycle events.
    this.signClient.on("session_delete", ({ topic }) => {
      if (this.session?.topic === topic) this.session = null;
    });
    this.signClient.on("session_expire", ({ topic }) => {
      if (this.session?.topic === topic) this.session = null;
    });
    this.signClient.on("session_update", ({ topic, params }) => {
      if (this.session?.topic === topic && params?.namespaces) {
        this.session = { ...this.session, namespaces: params.namespaces };
      }
    });
  }

  getSignClient(): ISignClient {
    return this.signClient;
  }

  getUserId(): string {
    return this.userId;
  }

  getMetadata(): SignClientTypes.Metadata {
    return this.signClient.metadata;
  }

  getPairing(): PairingTypes.Struct | null {
    return this.pairing;
  }

  getSession(): SessionTypes.Struct | null {
    return this.session;
  }

  getTopic(): string | null {
    return this.session?.topic ?? this.pairing?.topic ?? null;
  }

  getProposals(): ProposalTypes.Struct[] {
    return this.signClient.proposal.getAll();
  }

  // Subscribe to any `SignClient` event. Returns an unsubscribe function.
  on<E extends SignClientTypes.Event>(
    event: E,
    listener: (args: SignClientTypes.EventArguments[E]) => void,
  ): () => void {
    this.signClient.on(event, listener);
    return () => this.signClient.off(event, listener);
  }

  async connect(
    params: EngineTypes.ConnectParams = { ...DEFAULT_CONNECT_PARAMS },
    newConnection: boolean = false,
    successCallback?: WcSuccessCallback,
    errorCallback?: WcErrorCallback,
  ): Promise<string | undefined> {
    if (!params.sessionProperties) {
      params.sessionProperties = {
        [VIA_SESSION_OPTIONS_KEY]: JSON.stringify({}),
      };
    }

    if (this.pairing && !newConnection) {
      params.pairingTopic = this.pairing.topic;
    }

    const { uri, approval } = await this.signClient.connect(params);

    // Fire and forget: awaiting `approval()` blocks until the wallet
    // responds, so caller code (rendering the QR) must not wait on it.
    void approval()
      .then((session) => {
        this.session = session;
        successCallback?.(params, session);
      })
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        errorCallback?.(params, error);
      });

    return uri;
  }

  // Send a JSON-RPC request over the active session (e.g. `stellar_signXDR`).
  async sendRequest(
    chainId: string,
    request: EngineTypes.RequestParams["request"],
    successCallback?: WcRequestSuccessCallback,
    errorCallback?: WcRequestErrorCallback,
  ): Promise<void> {
    const session = this.session ?? this.signClient.session.getAll().pop() ?? null;
    if (!session) {
      throw new Error("No active WalletConnect session");
    }

    const params: EngineTypes.RequestParams = {
      topic: session.topic,
      chainId,
      request,
    };

    this.signClient
      .request(params)
      .then((response) => successCallback?.(params, response))
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        errorCallback?.(params, error);
      });
  }

  // Disconnect the active session (or a specific topic if provided).
  async disconnect(
    topic: string | undefined = this.session?.topic,
    reason: EngineTypes.DisconnectParams["reason"] = {
      code: 6000,
      message: "User disconnected",
    },
  ): Promise<void> {
    if (!topic) return;
    await this.signClient.disconnect({ topic, reason });
    if (this.session?.topic === topic) {
      this.session = null;
    }
  }
}
